<template>
<q-drawer
    show-if-above
    v-model="rightDrawerOpen"
    class="sidebar sidebar-right"
    side="right"

    :mini="!rightDrawerOpen || miniState"
    :mini-width="64"

    :width="width"
    :breakpoint="600"
    :overlay="overlay"
>
  <!-- drawer content -->
  <div class="row full-height">
    <div class="sidebar-right__navigation full-height">
      <icon-button
          id="miniState ? 'sidebar-close' : 'sidebar-open'"
          :class="{'rotate-180': miniState}"
          icon="double_arrow"
          :aria-label="miniState ? 'Open rechter sidebar' : 'Sluit rechter sidebar'"
          @click="toggleSidebar(null)"
      />
      <icon-button
          v-if="projectComments.comments.data"
          icon="chat_bubble_outline"
          aria-label="Bekijk berichten"
          @click="toggleSidebar('comments')"
          show-notify-badge
      />
      <icon-button
          v-if="hasRuleVersions"
          icon="schedule"
          aria-label="Bekijk versies"
          @click="toggleSidebar('versions')"
      />
    </div>
    <div class="sidebar-right__content q-mini-drawer-hide full-height col-grow">
      <div class="column full-height">
        <div class="col-shrink sidebar__header">
          <div class="row items-center q-px-lg">
            <div v-if="panel === 'comments'" class="heading-4">Opmerkingen</div>
            <div v-if="panel === 'versions'" class="heading-4">Regel versies</div>
            <q-space />
            <template v-if="panel === 'comments'">
              <comment-filter v-if="maxiState"
                              :commenters="commenters"
              />
              <icon-button
                  v-if="$can('saveComment', project)"
                  icon="add_comment"
                  aria-label="Plaats bericht"
                  color="neutral-70"
                  class="flip-horizontal q-mr-sm"
                  @click="showCreateForm = true"
                  dense
              />
            </template>

            <template v-if="this.maxiState">
              <icon-button
                  icon="close_fullscreen"
                  class="rotate-90"
                  aria-label="Minimaliseer sidebar"
                  color="neutral-70"
                  @click="resizeSidebar"
                  dense
              />
            </template>
            <template v-else>
              <icon-button
                  icon="open_in_full"
                  class="rotate-90"
                  :aria-label="hasFilteredComments ? `Maximaliseer sidebar om filters te bewerken` : `Maximaliseer sidebar`"
                  color="neutral-70"
                  @click="resizeSidebar"
                  dense
              >
                <q-badge color="warning" rounded floating v-if="hasFilteredComments" />
              </icon-button>
            </template>
          </div>
        </div>
        <div class="col">
          <scroll-area class="fit">
            <q-tab-panels v-model="panel">
              <q-tab-panel name="comments" :style="maxiState ? '' : 'width: 287px'">
                <comment-create v-model:createForm="showCreateForm"
                                :current-user="currentUser"
                                :project-id="project.id"
                                :phase-id="phase.id"
                                :rule-id="ruleVersion?.rule_id"
                                :object-id="object?.id"
                                :rule-version-id="ruleVersion.id" />
                <comment-list
                    v-if="projectComments.comments.data && projectComments.comments.data.length > 0"
                    :comments="projectComments.comments"
                    :current-user="currentUser"
                    :project-id="project.id"
                    :max-view="maxiState"
                />
                <div
                    v-else-if="projectComments.comments.data && !projectComments.comments.data.length && !showCreateForm"
                    class="q-pa-lg">
                    <span v-if="hasFilteredComments">
                      Er zijn geen berichten gevonden binnen de opgegeven filter
                    </span>
                  <span v-else>
                      Er zijn nog geen berichten geplaatst
                    </span>
                </div>
              </q-tab-panel>
              <q-tab-panel name="versions">
                <div>
                  <q-list separator>
                    <q-item v-for="version in ruleVersions"
                            :key="version.id"
                            clickable
                            :disable="version.id === ruleVersion.id"
                            @click="$goto('rule.show_version', {rule_id: rule.id, version_id: version.id})">
                      <q-item-section avatar>
                        <q-avatar v-if="version.isActiveVersion"
                                  color="accent"
                                  text-color="white">
                          <q-tooltip>Huidige versie</q-tooltip>
                          <strong>{{ version.version }}</strong>
                        </q-avatar>
                        <q-avatar v-else-if="!version.is_published"
                                  color="white"
                                  text-color="primary">
                          <q-tooltip>Versie in concept</q-tooltip>
                          <strong>{{ version.version }}</strong>
                        </q-avatar>
                        <q-avatar v-else
                                  color="primary"
                                  text-color="white">
                          <q-tooltip>Oudere versie</q-tooltip>
                          {{ version.version }}
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="ellipsis">{{ version.summary }}</q-item-label>
                        <q-item-label caption>Laatst bewerkt: {{ $formatDateStandard(version.updated_at) }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </div>
              </q-tab-panel>
            </q-tab-panels>
          </scroll-area>
        </div>
      </div>
    </div>
  </div>
</q-drawer>
</template>


<script>
import CommentCreate from '../comment/CommentCreate'
import { useProjectComents } from '../../stores/useProjectComments'

export default {
  setup (props) {
    const projectComments = useProjectComents()
    projectComments.load(props.project.id, props.phase?.id, props.ruleVersion?.rule_id, props.object?.id)
    return {
      projectComments,
    }
  },
  components: {
    CommentCreate,
  },
  props: {
    innerComponent: {
      type: String,
    },
    pageData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    project: {
      type: Object,
      default: () => {
        return {}
      },
    },
    rule: {
      type: Object,
      default: () => {
        return {}
      },
    },
    ruleVersion: {
      type: Object,
      default: () => {
        return {}
      },
    },
    phase: {
      type: Object,
      default: () => {
        return {}
      },
    },
    object: {
      type: Object,
      default: () => {
        return {}
      },
    },
    commenters: {
      type: Array,
      default: () => [],
    },
    breakPoint: {
      type: Number,
      default: 1420,
    },
  },
  data () {
    return {
      panel: this.project?.id ? 'comments' : 'versions',
      rightDrawerOpen: true,
      miniState: false,
      maxiState: false,
      overlay: false,
      width: 350,
      showCreateForm: false,
    }
  },
  computed: {
    hasRuleVersions () {
      return this.rule?.versions?.length
    },
    hasFilteredComments () {
      if (this.panel !== 'comments') {
        return false
      }
      return this.projectComments.filter.length > 0
    },
    ruleVersions () {
      if (!this.hasRuleVersions) return []
      let activeFound
      return [...this.rule.versions].reverse()
          .map(version => {
            if (activeFound || !version.is_published) return version
            activeFound = true
            return {
              isActiveVersion: true,
              ...version,
            }
          })
    },
  },
  methods: {
    toggleSidebar (panel = null) {
      if (panel) {
        this.panel = panel
        this.miniState = false
      } else if (this.miniState) {
        this.miniState = false
      } else {
        this.closeSidebar()
      }
      this.$forceUpdate()
    },
    closeSidebar () {
      this.miniState = true
      this.maxiState = false
      this.overlay = false
      this.width = 350
    },
    resizeSidebar () {
      const maxSidebarWidth = this.calculateOffsetForMaxSidebar()
      this.maxiState = !this.maxiState

      if (this.maxiState) {
        this.overlay = true
        this.width = maxSidebarWidth
        this.miniState = false
      } else {
        this.width = 350
        this.overlay = false
      }
    },
    calculateOffsetForMaxSidebar () {
      const sidebarOffset = 350

      if (this.maxiState) {
        return 0
      }

      let pageWidth = document.querySelector('.main').offsetWidth
      let padding = this.$isDesktop ? 24 : 0

      return sidebarOffset + (pageWidth - padding)
    },
    checkSize () {
      this.miniState = document.body.clientWidth < this.breakPoint
    },
  },
  mounted () {
    this.checkSize()
    window.addEventListener('resize', this.checkSize)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.checkSize)
  },
}
</script>
